import { STANDIN_CURRENCIES, allCoinParts, coinCount } from "odyssey-shared";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";

/**
 * The wallet line's width, worked out rather than measured (P2.8-T53, ADR-125's note for P2.8-T53, item 5): each mark
 * is its box, then the gap between mark and number and its number in the pixel face's `number` role, whose glyph
 * advances `pixelTextWidth` reads at 1x; the coin parts stand apart by the part gap and the currencies by the
 * currency gap. A bare stand-in is its box alone.
 */

/**
 * The line's geometry in logical pixels, as `line.tsx` draws it: each mark's box, the gap between a mark and its
 * number, between the coin parts and between currencies.
 */
export const WALLET_GEOMETRY = { box: 16, coinGap: 2, partGap: 4, currencyGap: 6 } as const;

/**
 * The number a stand-in currency draws while no system earns it.
 */
export const STANDIN_COUNT = "0";

/**
 * One mark's width: its box, then the gap and its number when it draws one.
 *
 * @param count The number drawn, or null for a mark drawn bare.
 * @returns The width in logical pixels.
 */
function markWidth(count: string | null): number {
  return WALLET_GEOMETRY.box + (count === null ? 0 : WALLET_GEOMETRY.coinGap + pixelTextWidth(count));
}

/**
 * The wallet line's width for a purse: Valish coin's three parts, then the stand-in currencies, with their 0 or bare.
 *
 * @param copper The purse in copper.
 * @param bare Whether the stand-ins draw their pictures without their 0.
 * @returns The width in logical pixels.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 * @example
 * walletLineWidth(34512, false);
 * // 177
 */
export function walletLineWidth(copper: number, bare: boolean): number {
  const parts = allCoinParts(copper);
  let width = (parts.length - 1) * WALLET_GEOMETRY.partGap;
  for (const part of parts) {
    width += markWidth(coinCount(part));
  }
  for (let index = 0; index < STANDIN_CURRENCIES.length; index += 1) {
    width += WALLET_GEOMETRY.currencyGap + markWidth(bare ? null : STANDIN_COUNT);
  }
  return width;
}
